/**
 * Mobile — when page sections enter the viewport, ease scroll so the section
 * top aligns with the viewport top (visual top = 0, not scroll-margin-top).
 * Forward scroll only; upward scroll cancels an in-flight snap (no lock).
 */
import { isMobileAnchorNavigationActive } from "@/mobileAnchorNav";
import { isPastHeroForSectionSnap } from "@/mobileScrollBootstrap";
import {
  afterTouchRelease,
  getMobileViewportHeight,
} from "@/mobileSafariScroll";
import {
  EASE_SNAP_FAST,
  MOBILE_GATE_SNAP_DURATION_SEC,
  MOBILE_SECTION_SNAP_DURATION_SEC,
  MOBILE_SECTION_SNAP_MAX_TRAVEL_VH,
  MOBILE_SECTION_SNAP_MIN_DURATION_SEC,
  prefersReducedMotion,
} from "@/scrollPresets";

export const MOBILE_SNAP_SELECTOR = "[data-sp-snap-section]";

/** Sections excluded from viewport-top snap. */
const SNAP_EXCLUDED_SECTION_IDS = new Set([
  "hero",
  "programs",
  "founders",
  "footer",
]);

const SNAP_TOP_TOLERANCE_PX = 4;
export const SNAP_DURATION_SEC = MOBILE_SECTION_SNAP_DURATION_SEC;
const SNAP_SETTLE_MS = 0;
const SNAP_SETTLE_VEL = 0.35;
/** Viewport enter — fire snap promptly without waiting for full momentum decay. */
const SNAP_ENTER_SETTLE_VEL = 10.5;
const SNAP_SETTLE_POLL_MS = 16;
const SNAP_ENTER_POLL_MS = 8;
const SNAP_MIN_TRAVEL_PX = 4;
const SNAP_MAX_RETRIES = 3;

let snappingActive = false;

export function isMobileSectionSnapping() {
  return snappingActive;
}

export function shouldBlockMobileTouchCoast() {
  return snappingActive;
}

/** Skip post-release coast when a forward snap will run (avoids double ease + jitter). */
export function shouldSkipMobileTouchCoast(lenis, releaseVy = 0) {
  if (snappingActive || prefersReducedMotion() || releaseVy < 0) return snappingActive;
  if (isMobileAnchorNavigationActive()) return false;
  const candidate = findMobileSectionSnapCandidate();
  if (!candidate || !passesHeroSnapGate(candidate, lenis)) return false;
  return true;
}

export function sectionSnapScrollY(el, lenis) {
  const scroll = lenis?.scroll ?? window.scrollY ?? 0;
  return scroll + el.getBoundingClientRect().top;
}

export function isSectionAlignedAtViewportTop(el) {
  return Math.abs(el.getBoundingClientRect().top) <= SNAP_TOP_TOLERANCE_PX;
}

export function isSnapEligibleSection(el) {
  return Boolean(el?.id && !SNAP_EXCLUDED_SECTION_IDS.has(el.id));
}

export function sectionNeedsViewportSnap(el) {
  if (!isSnapEligibleSection(el)) return false;
  if (isSectionAlignedAtViewportTop(el)) return false;
  const vh = getMobileViewportHeight() || 1;
  const rect = el.getBoundingClientRect();
  if (rect.bottom <= 0 || rect.top >= vh) return false;
  return rect.top > SNAP_TOP_TOLERANCE_PX;
}

export function findMobileSectionSnapCandidate(
  sections = [...document.querySelectorAll(MOBILE_SNAP_SELECTOR)],
) {
  let best = null;
  let bestTop = Infinity;

  for (const el of sections) {
    if (!isSnapEligibleSection(el) || !sectionNeedsViewportSnap(el)) continue;
    const top = el.getBoundingClientRect().top;
    if (top < bestTop) {
      bestTop = top;
      best = el;
    }
  }

  return best;
}

/** Hero → journey: journey is visible but its top is not yet at the viewport top. */
function isHeroToJourneySnap(el) {
  return el?.id === "journey" && sectionNeedsViewportSnap(el);
}

/** Journey may snap while still on the hero; other sections wait until past hero. */
function passesHeroSnapGate(el, lenis) {
  return isPastHeroForSectionSnap(lenis) || isHeroToJourneySnap(el);
}

function snapDurationForTravel(travelPx) {
  const vh = window.innerHeight || 800;
  const span = Math.max(vh * MOBILE_SECTION_SNAP_MAX_TRAVEL_VH, 1);
  const t = Math.min(1, travelPx / span);
  return (
    MOBILE_SECTION_SNAP_MIN_DURATION_SEC +
    (MOBILE_SECTION_SNAP_DURATION_SEC - MOBILE_SECTION_SNAP_MIN_DURATION_SEC) * t
  );
}

/**
 * @param {import('lenis').default} lenis
 * @returns {() => void}
 */
export function attachMobileSectionSnap(lenis) {
  let sections = [];
  let io = null;
  let lastScrollY = lenis.scroll;
  let scrollDir = 0;
  let touchActive = false;
  let lastTouchY = null;
  let lastTouchVy = 0;
  let releaseHandledAt = 0;
  let snapTimer = 0;
  let snapFallbackTimer = 0;
  let snapRetryTimer = 0;
  let snapRetryCount = 0;
  const alignedSections = new Set();
  const inView = new Set();
  let pendingEl = null;
  let activeSnapEl = null;
  let snapFinished = false;

  const collectSections = () => {
    sections = [...document.querySelectorAll(MOBILE_SNAP_SELECTOR)].filter(
      isSnapEligibleSection,
    );
  };

  const clampSnapY = (el) => {
    const limit = lenis.limit ?? Infinity;
    return Math.max(0, Math.min(limit, sectionSnapScrollY(el, lenis)));
  };

  const releaseLenisSnapState = () => {
    if (typeof lenis.reset === "function") {
      lenis.reset();
    }
  };

  const releaseSnapAnimation = () => {
    snappingActive = false;
    activeSnapEl = null;
    window.clearTimeout(snapFallbackTimer);
  };

  const correctSectionTop = (el, immediate = true) => {
    if (!el) return;
    const y = clampSnapY(el);
    lenis.scrollTo(y, {
      immediate,
      duration: immediate ? undefined : MOBILE_GATE_SNAP_DURATION_SEC * 0.5,
      easing: EASE_SNAP_FAST,
      lerp: undefined,
      force: true,
      programmatic: true,
      lock: false,
    });
    releaseLenisSnapState();
  };

  const markAlignedIfFlush = (el) => {
    if (!el) return;
    if (isSectionAlignedAtViewportTop(el)) {
      alignedSections.add(el);
    }
  };

  const canRetrySnapSection = (el) => {
    if (!el || prefersReducedMotion() || snappingActive || touchActive) return false;
    if (isMobileAnchorNavigationActive()) return false;
    if (!sectionNeedsViewportSnap(el)) return false;
    if (!passesHeroSnapGate(el, lenis)) return false;
    return true;
  };

  const retrySnapIfNeeded = (el, delayMs = 48) => {
    if (!canRetrySnapSection(el) || snapRetryCount >= SNAP_MAX_RETRIES) return;
    alignedSections.delete(el);
    snapRetryCount += 1;
    window.clearTimeout(snapRetryTimer);
    snapRetryTimer = window.setTimeout(() => {
      if (!canRetrySnapSection(el)) return;
      snapFinished = false;
      snapToTop(el, true);
    }, delayMs);
  };

  const completeSnap = (el, interrupted = false) => {
    if (snapFinished) return;
    snapFinished = true;
    releaseSnapAnimation();

    if (!el || interrupted) {
      releaseLenisSnapState();
      retrySnapIfNeeded(el);
      return;
    }

    const drift = Math.abs(el.getBoundingClientRect().top);
    if (drift > SNAP_TOP_TOLERANCE_PX) {
      releaseLenisSnapState();
      snapFinished = false;
      if (drift > 32) {
        if (snapRetryCount >= SNAP_MAX_RETRIES) {
          correctSectionTop(el, true);
          markAlignedIfFlush(el);
          snapRetryCount = 0;
          return;
        }
        snapRetryCount += 1;
        requestAnimationFrame(() => snapToTop(el, true));
        return;
      }
      correctSectionTop(el, true);
      markAlignedIfFlush(el);
      snapRetryCount = 0;
      return;
    }

    markAlignedIfFlush(el);
    snapRetryCount = 0;
  };

  const hasForwardScrollIntent = () =>
    scrollDir > 0 || lastTouchVy > 2;

  const canSnapSection = (el) => {
    if (!el || prefersReducedMotion() || snappingActive || touchActive) {
      return false;
    }
    if (isMobileAnchorNavigationActive()) return false;
    if (alignedSections.has(el)) return false;
    if (!sectionNeedsViewportSnap(el)) return false;
    if (!passesHeroSnapGate(el, lenis)) return false;
    if (!hasForwardScrollIntent()) return false;
    return true;
  };

  const interruptSnap = () => {
    if (!snappingActive) return;
    completeSnap(activeSnapEl, true);
  };

  const snapToTop = (el, isRetry = false) => {
    if (isRetry) {
      if (!canRetrySnapSection(el)) return;
    } else {
      snapRetryCount = 0;
      if (!canSnapSection(el)) return;
    }

    const y = clampSnapY(el);
    const travel = Math.abs(y - lenis.scroll);
    if (travel < SNAP_MIN_TRAVEL_PX) {
      correctSectionTop(el, true);
      markAlignedIfFlush(el);
      return;
    }

    releaseLenisSnapState();
    if (lenis.isStopped && typeof lenis.start === "function") {
      lenis.start();
    }

    const duration = snapDurationForTravel(travel);
    snappingActive = true;
    snapFinished = false;
    activeSnapEl = el;
    window.clearTimeout(snapRetryTimer);

    lenis.scrollTo(y, {
      duration,
      lerp: undefined,
      easing: EASE_SNAP_FAST,
      lock: false,
      force: true,
      programmatic: true,
      onComplete: () => completeSnap(el, false),
    });
    snapFallbackTimer = window.setTimeout(
      () => completeSnap(el, false),
      duration * 1000 + 180,
    );
  };

  const trySettledSnap = (fast = false) => {
    const el = pendingEl;
    if (!el) return;
    if (touchActive) {
      if (!passesHeroSnapGate(el, lenis) || !sectionNeedsViewportSnap(el)) {
        pendingEl = null;
        return;
      }
      const pollMs = fast ? SNAP_ENTER_POLL_MS : SNAP_SETTLE_POLL_MS;
      snapTimer = window.setTimeout(() => trySettledSnap(fast), pollMs);
      return;
    }
    if (!canSnapSection(el)) {
      pendingEl = null;
      return;
    }
    const vel = Math.abs(lenis.velocity ?? 0);
    const settleVel = fast ? SNAP_ENTER_SETTLE_VEL : SNAP_SETTLE_VEL;
    if (vel > settleVel) {
      const pollMs = fast ? SNAP_ENTER_POLL_MS : SNAP_SETTLE_POLL_MS;
      snapTimer = window.setTimeout(() => trySettledSnap(fast), pollMs);
      return;
    }
    pendingEl = null;
    snapToTop(el);
  };

  const scheduleSnap = (el, fast = false) => {
    if (!el || alignedSections.has(el)) return;
    pendingEl = el;
    window.clearTimeout(snapTimer);
    if (fast) {
      requestAnimationFrame(() => trySettledSnap(true));
      return;
    }
    snapTimer = window.setTimeout(() => trySettledSnap(false), SNAP_SETTLE_MS);
  };

  const onSectionEnter = (el) => {
    if (alignedSections.has(el)) return;
    if (scrollDir < 0 || !passesHeroSnapGate(el, lenis)) return;
    scheduleSnap(el, true);
  };

  const onIntersect = (entries) => {
    for (const entry of entries) {
      const el = entry.target;
      if (entry.isIntersecting) {
        const entering = !inView.has(el);
        inView.add(el);
        if (entering) onSectionEnter(el);
      } else {
        inView.delete(el);
        alignedSections.delete(el);
      }
    }
  };

  const onLenisScroll = () => {
    const y = lenis.scroll;
    if (snappingActive && !touchActive) {
      lastScrollY = y;
      return;
    }
    if (y > lastScrollY + 0.5) scrollDir = 1;
    else if (y < lastScrollY - 0.5) {
      scrollDir = -1;
      if (snappingActive) interruptSnap();
      pendingEl = null;
      window.clearTimeout(snapTimer);
    }
    lastScrollY = y;
  };

  const snapBestCandidate = (immediate = false) => {
    if (!hasForwardScrollIntent()) return;
    const candidate = findMobileSectionSnapCandidate(sections);
    if (!candidate || !passesHeroSnapGate(candidate, lenis)) return;
    if (immediate) {
      pendingEl = null;
      window.clearTimeout(snapTimer);
      snapToTop(candidate);
      return;
    }
    scheduleSnap(candidate);
  };

  const onTouchStart = (e) => {
    touchActive = true;
    lastTouchY = e.touches?.[0]?.clientY ?? null;
    lastTouchVy = 0;
    pendingEl = null;
    snapRetryCount = 0;
    window.clearTimeout(snapTimer);
    window.clearTimeout(snapRetryTimer);
    if (snappingActive) interruptSnap();
  };

  const onTouchMove = (e) => {
    if (!touchActive) return;
    const y = e.touches?.[0]?.clientY;
    if (y == null) return;
    if (lastTouchY != null) lastTouchVy = lastTouchY - y;
    lastTouchY = y;
  };

  const onTouchRelease = () => {
    const now = performance.now();
    if (now - releaseHandledAt < 48) return;
    releaseHandledAt = now;
    touchActive = false;
    if (lastTouchVy > 6) scrollDir = 1;
    else if (lastTouchVy < -6) scrollDir = -1;
    lastTouchY = null;

    const runSnap = () => snapBestCandidate(true);
    afterTouchRelease(runSnap);
  };

  collectSections();
  if (!sections.length) return () => {};

  let ioReady = false;
  const onIntersectSafe = (entries) => {
    if (!ioReady) return;
    onIntersect(entries);
  };

  if (typeof IntersectionObserver !== "undefined") {
    io = new IntersectionObserver(onIntersectSafe, {
      root: null,
      rootMargin: "0px",
      threshold: [0, 0.01, 0.05, 0.1, 0.2],
    });
    sections.forEach((el) => io.observe(el));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ioReady = true;
      });
    });
  } else {
    ioReady = true;
  }

  lenis.on("scroll", onLenisScroll);

  const onResize = () => collectSections();
  const touchOpts = { passive: true, capture: true };
  document.addEventListener("touchstart", onTouchStart, touchOpts);
  document.addEventListener("touchmove", onTouchMove, touchOpts);
  document.addEventListener("touchend", onTouchRelease, touchOpts);
  document.addEventListener("touchcancel", onTouchRelease, touchOpts);
  document.addEventListener("pointerup", onTouchRelease, touchOpts);
  window.addEventListener("resize", onResize, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onResize, { passive: true });
  }

  return () => {
    lenis.off("scroll", onLenisScroll);
    io?.disconnect();
    inView.clear();
    alignedSections.clear();
    completeSnap(null, true);
    window.clearTimeout(snapTimer);
    window.clearTimeout(snapRetryTimer);
    document.removeEventListener("touchstart", onTouchStart, touchOpts);
    document.removeEventListener("touchmove", onTouchMove, touchOpts);
    document.removeEventListener("touchend", onTouchRelease, touchOpts);
    document.removeEventListener("touchcancel", onTouchRelease, touchOpts);
    document.removeEventListener("pointerup", onTouchRelease, touchOpts);
    window.removeEventListener("resize", onResize);
    window.visualViewport?.removeEventListener("resize", onResize);
  };
}
